/**
 * Composant de gestion du plan et de la facturation pour une organisation
 * - Affiche le plan actuel (FREEMIUM / PRO)
 * - Affiche la commission CashLive dynamique
 * - Affiche le bonus de bienvenue restant
 * - Boutons d'action : Passer au PRO, Gérer l'abonnement
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    collection,
    query,
    where,
    limit,
    getDocs,
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';
import {
    Crown,
    Gift,
    TrendingUp,
    ExternalLink,
    CheckCircle,
    Sparkles,
    Zap,
    MessageSquare,
    Loader2,
    Brain,
    ShoppingCart,
    Infinity,
    ArrowRight,
    Clock,
    FileText,
    ChevronDown,
    ChevronRight,
} from 'lucide-react';

import { Card, CardBody, Badge, Button } from '@/components/ui';
import { useOrgaConfigurationContext } from '@/app/providers';
import { useUserData } from '@/shared/hooks/useUserData';
import { useAuth } from '@/app/providers/AuthContext';
import { SUBSCRIPTION_PLANS } from '@/shared/constants/subscription';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { isLiveAssistantEnabled } from '@/shared/config/featureFlags';
import { BuyCreditsModal } from './BuyCreditsModal';

// Types
type PlanType = 'FREEMIUM' | 'PRO' | 'LAUNCH_OFFER';

interface SubscriptionPlanSectionProps {
    onUpgradeToPro?: () => void;
    onManageSubscription?: () => void;
}

interface BillingStatement {
    id: string;
    periodMonth: string;
    subtotal: number;
    vatAmount: number;
    total: number;
    totalFormatted: string;
    status: string;
    createdAt?: string | Date;
    paidAt?: string | Date;
    invoice?: {
        invoiceId?: string;
        invoiceNumber?: string;
        invoiceStatus?: string;
        stripeInvoiceId?: string | null;
        stripeHostedInvoiceUrl?: string | null;
        stripePdfUrl?: string | null;
        stripeInvoiceStatus?: string | null;
        totalCents?: number;
        paidAt?: string | Date | null;
    } | null;
}

interface InternalInvoice {
    id: string;
    invoiceNumber: string;
    type: 'commissions_monthly' | 'credits_purchase' | 'shipping_payg';
    status: string;
    issueDate: string | Date;
    periodLabel?: string;
    totalCents: number;
    totalFormatted: string;
    subtotalCents: number;
    vatAmountCents: number;
    stripeHostedInvoiceUrl?: string | null;
    stripePdfUrl?: string | null;
    stripeInvoiceId?: string | null;
    stripeInvoiceStatus?: string | null;
    paidAt?: string | Date | null;
    paymentIntentId?: string | null;
}

// Unified credits balance interface
interface UnifiedCreditsBalance {
    balanceCents: number;
    totalPurchasedCents: number;
    totalUsedCents: number;
    totalUsedForShippingCents: number;
    totalUsedForAICents: number;
}

const SubscriptionPlanSection: React.FC<SubscriptionPlanSectionProps> = ({
    onUpgradeToPro,
    onManageSubscription,
}) => {
    const { t, i18n } = useTranslation();
    const { idorga } = useParams<{ idorga: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { configuration, loading: configLoading, refreshConfiguration } = useOrgaConfigurationContext();
    const { isSuperAdmin } = useUserData();
    const { currentUser } = useAuth();
    const [isUpgrading, setIsUpgrading] = useState(false);

    // Billing history (monthly statements + all invoices)
    const [billingStatements, setBillingStatements] = useState<BillingStatement[]>([]);
    const [allInvoices, setAllInvoices] = useState<InternalInvoice[]>([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [expandedStatementId, setExpandedStatementId] = useState<string | null>(null);
    const [expandedMonths, setExpandedMonths] = useState<Set<string>>(() => {
        const now = new Date();
        return new Set([`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`]);
    });

    // Unified credits state
    const [unifiedCredits, setUnifiedCredits] = useState<UnifiedCreditsBalance | null>(null);
    const [creditsLoading, setCreditsLoading] = useState(false);

    // Buy credits modal state
    const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

    // Super Admin: État pour le formulaire d'abonnement personnalisé
    const [showSuperAdminPanel, setShowSuperAdminPanel] = useState(false);
    const [isSavingCustomPlan, setIsSavingCustomPlan] = useState(false);
    const [inputCustomPlanName, setInputCustomPlanName] = useState('');
    const [inputCustomPrice, setInputCustomPrice] = useState(0);
    const [inputCustomCommissionRate, setInputCustomCommissionRate] = useState(3.0);
    const [inputCustomFeatures, setInputCustomFeatures] = useState({
        iaAssistant: true,
        savChat: true,
        savHistory: true,
    });
    const [inputCustomPlanNote, setInputCustomPlanNote] = useState('');
    const [inputCustomPlanExpiresAt, setInputCustomPlanExpiresAt] = useState<string>('');

    // Fetch unified credits balance
    const fetchUnifiedCredits = useCallback(async () => {
        const orgId = configuration?.organizationId || idorga;
        if (!orgId) return;

        try {
            setCreditsLoading(true);
            const response = await fetch(
                `${FUNCTIONS_BASE_URL}/appCreditsBalance?organizationId=${orgId}`
            );
            const data = await response.json();
            if (data.success) {
                setUnifiedCredits(data.balance);
            }
        } catch (error) {
            console.error('Error fetching unified credits:', error);
        } finally {
            setCreditsLoading(false);
        }
    }, [configuration?.organizationId, idorga]);

    // Fetch credits on mount and when configuration changes
    useEffect(() => {
        fetchUnifiedCredits();
    }, [fetchUnifiedCredits]);

    // Fetch billing statements + all invoices en parallèle
    const fetchBillingStatements = useCallback(async () => {
        const orgId = configuration?.organizationId || idorga;
        if (!orgId) return;

        try {
            setBillingLoading(true);

            // Fetch monthly statements via HTTP (function déjà déployée)
            const statementsPromise = fetch(`${FUNCTIONS_BASE_URL}/billingStatements?orgId=${orgId}&limit=12`)
                .then(r => r.ok ? r.json() : null)
                .catch(() => null);

            // Fetch invoices DIRECTEMENT depuis Firestore (pas besoin de function déployée)
            // Tri côté client pour éviter un index composite pendant la construction
            const invoicesPromise = getDocs(
                query(
                    collection(db, 'invoices'),
                    where('organizationId', '==', orgId),
                    limit(50)
                )
            ).then(snap =>
                snap.docs.map(doc => {
                    const d = doc.data();
                    const totalCents: number = d.totalCents ?? 0;
                    return {
                        id: doc.id,
                        invoiceNumber: d.invoiceNumber,
                        type: d.type,
                        status: d.status,
                        issueDate: d.issueDate?.toDate?.() ?? d.issueDate,
                        periodLabel: d.periodLabel ?? null,
                        totalCents,
                        totalFormatted: `${(totalCents / 100).toFixed(2)} €`,
                        subtotalCents: d.subtotalCents ?? 0,
                        vatAmountCents: d.vatAmountCents ?? 0,
                        stripeHostedInvoiceUrl: d.stripeHostedInvoiceUrl ?? null,
                        stripePdfUrl: d.stripePdfUrl ?? null,
                        stripeInvoiceId: d.stripeInvoiceId ?? null,
                        stripeInvoiceStatus: d.stripeInvoiceStatus ?? null,
                        paidAt: d.paidAt?.toDate?.() ?? d.paidAt ?? null,
                        paymentIntentId: d.paymentIntentId ?? null,
                    } as InternalInvoice;
                })
                // Tri par date décroissante côté client (évite l'index composite pendant construction)
                .sort((a, b) => {
                    const dateA = a.issueDate ? new Date(a.issueDate).getTime() : 0;
                    const dateB = b.issueDate ? new Date(b.issueDate).getTime() : 0;
                    return dateB - dateA;
                })
            ).catch((err) => {
                console.error('Error fetching invoices from Firestore:', err);
                return [] as InternalInvoice[];
            });

            const [statementsData, invoices] = await Promise.all([statementsPromise, invoicesPromise]);

            if (statementsData?.statements && Array.isArray(statementsData.statements)) {
                setBillingStatements(statementsData.statements);
            } else {
                setBillingStatements([]);
            }

            setAllInvoices(invoices);
        } catch (error) {
            console.error('Error fetching billing data:', error);
            setBillingStatements([]);
            setAllInvoices([]);
        } finally {
            setBillingLoading(false);
        }
    }, [configuration?.organizationId, idorga]);

    useEffect(() => {
        fetchBillingStatements();
    }, [fetchBillingStatements]);

    // Détection du retour de paiement réussi
    useEffect(() => {
        const subscriptionStatus = searchParams.get('subscription');
        if (subscriptionStatus === 'success') {
            toast.success(t('admin.subscription.paymentSuccess', 'Paiement réussi ! Votre abonnement est maintenant actif.'));
            // Nettoyer l'URL
            searchParams.delete('subscription');
            setSearchParams(searchParams, { replace: true });
            // Refetch pour avoir les données à jour
            refreshConfiguration();
            fetchUnifiedCredits();
        } else if (subscriptionStatus === 'canceled') {
            toast.error(t('admin.subscription.paymentCanceled', 'Paiement annulé.'));
            searchParams.delete('subscription');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, t, refreshConfiguration, fetchUnifiedCredits]);

    // Loading combiné
    const loading = configLoading;

    // Extraire les données de configuration d'abonnement
    const subscriptionConfig = configuration?.subscriptionConfig;
    const planType: PlanType = (subscriptionConfig?.planType as PlanType) || 'FREEMIUM';
    const plan = SUBSCRIPTION_PLANS[planType] || SUBSCRIPTION_PLANS.FREEMIUM;

    // ========== DÉTECTION PLAN PERSONNALISÉ ==========
    const isCustomPlan = subscriptionConfig?.isCustomPlan === true;
    const customPlanName = subscriptionConfig?.customPlanName;
    const customPrice = subscriptionConfig?.customPrice;
    const customCommissionRate = subscriptionConfig?.customCommissionRate;

    // Prix affiché : custom si personnalisé, sinon standard
    const displayPrice = isCustomPlan && customPrice !== undefined && customPrice !== null ? customPrice : plan.price;
    const displayPlanName = isCustomPlan && customPlanName ? customPlanName : plan.name;

    // IA Assistant - Now using unified credits
    const isIAUnlimited = isCustomPlan
        ? (subscriptionConfig?.customFeatures?.iaAssistant ?? true)
        : (planType === 'PRO');

    // Use unified credits balance (cents converted to display)
    const creditsBalanceCents = unifiedCredits?.balanceCents ?? 0;
    const creditsUsedForAICents = unifiedCredits?.totalUsedForAICents ?? 0;
    const creditsUsedForShippingCents = unifiedCredits?.totalUsedForShippingCents ?? 0;

    // Commission CashLive effective
    // Calculer le taux en fonction du plan et de la date

    // Déterminer le taux de commission effectif
    let configuredCommissionRate: number;

    if (isCustomPlan && customCommissionRate !== undefined && customCommissionRate !== null) {
        // Plan personnalisé : utiliser le taux custom
        configuredCommissionRate = customCommissionRate;
    } else if (planType === 'LAUNCH_OFFER') {
        // Offre de lancement : 0% jusqu'au 30 mai 2026, puis 2,5%
        const LAUNCH_OFFER_END_DATE = new Date('2026-05-30T23:59:59');
        const now = new Date();
        configuredCommissionRate = now <= LAUNCH_OFFER_END_DATE ? 0 : 2.5;
    } else {
        // Plan standard (FREEMIUM/PRO) : utiliser le taux du plan
        configuredCommissionRate = plan.commissionRate;
    }

    // ========== OFFRE PERSONNALISÉE EN ATTENTE ==========
    const pendingOffer = subscriptionConfig?.pendingCustomOffer;
    const hasPendingOffer = pendingOffer && pendingOffer.status === 'pending';

    // Statut de l'abonnement
    const subscriptionStatus = subscriptionConfig?.subscriptionStatus;
    const isSubscriptionActive = subscriptionStatus === 'active';

    // Gérer le clic sur "Passer au plan PRO"
    const handleUpgradeToPro = async () => {
        if (onUpgradeToPro) {
            setIsUpgrading(true);
            try {
                await onUpgradeToPro();
            } catch (error) {
                toast.error(t('admin.subscription.upgradeError'));
                console.error('Erreur lors de la mise à niveau:', error);
            } finally {
                setIsUpgrading(false);
            }
        } else {
            // Créer une session Stripe Checkout pour l'abonnement PRO
            try {
                setIsUpgrading(true);
                const response = await fetch(`${FUNCTIONS_BASE_URL}/createSubscriptionCheckout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        organizationId: configuration?.organizationId || idorga,
                        planType: 'PRO',
                        returnUrl: window.location.href,
                    }),
                });

                const data = await response.json();

                if (data.checkoutUrl) {
                    window.location.href = data.checkoutUrl;
                } else {
                    throw new Error(data.error || 'Erreur lors de la création de la session');
                }
            } catch (error) {
                toast.error(t('admin.subscription.upgradeError', 'Erreur lors de la préparation de l\'abonnement'));
                console.error('Erreur upgrade:', error);
            } finally {
                setIsUpgrading(false);
            }
        }
    };

    // Navigate to the shipping credits page to purchase credits
    const handleBuyCredits = () => {
        setShowBuyCreditsModal(true);
    };

    const handleBuyCreditsSuccess = () => {
        // Refresh credits balance after purchase
        fetchUnifiedCredits();
    };

    // ========== SOUSCRIRE À L'OFFRE CUSTOM ==========
    // Quand un super admin a configuré un plan custom, le client doit y souscrire
    const handleSubscribeToCustomPlan = async () => {
        setIsUpgrading(true);
        try {
            // Appeler le backend pour créer une session checkout avec le prix custom
            const response = await fetch(`${FUNCTIONS_BASE_URL}/createCustomSubscriptionCheckout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    organizationId: configuration?.organizationId || idorga,
                    returnUrl: window.location.href,
                }),
            });

            const data = await response.json();

            if (data.checkoutUrl) {
                window.location.href = data.checkoutUrl;
            } else {
                console.error('Détails erreur backend:', data.details);
                throw new Error(data.error || 'Erreur lors de la création de la session');
            }
        } catch (error) {
            toast.error(t('admin.subscription.subscribeError', 'Erreur lors de la souscription à l\'offre'));
            console.error('Erreur souscription custom:', error);
        } finally {
            setIsUpgrading(false);
        }
    };

    // Gérer le clic sur "Gérer mon abonnement"
    const handleManageSubscription = async () => {
        if (onManageSubscription) {
            onManageSubscription();
        } else {
            // Créer une session Stripe Customer Portal
            try {
                const response = await fetch(`${FUNCTIONS_BASE_URL}/createCustomerPortalSession`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        organizationId: configuration?.organizationId || idorga,
                        returnUrl: window.location.href,
                    }),
                });

                const data = await response.json();

                if (data.portalUrl) {
                    window.location.href = data.portalUrl;
                } else {
                    throw new Error(data.error || 'Erreur lors de la création de la session');
                }
            } catch (error) {
                toast.error(t('admin.subscription.portalError'));
                console.error('Erreur lors de l\'accès au portail:', error);
            }
        }
    };

    if (loading) {
        return (
            <Card variant="default">
                <CardBody className="p-6 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </CardBody>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* En-tête de section */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                        <Crown className="w-5 h-5 text-primary" />
                        {t('admin.subscription.title', 'Plan & Facturation')}
                    </h2>
                    <p className="text-sm text-foreground-secondary mt-1">
                        {t('admin.subscription.description', 'Gérez votre abonnement CashLive et suivez vos commissions')}
                    </p>
                </div>
            </div>

            {/* Carte du plan actuel */}
            <Card variant="default" className={planType === 'PRO' ? 'border-primary/30 bg-primary/5' : ''}>
                <CardBody className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        {/* Informations du plan */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                                <Badge
                                    variant={planType === 'PRO' ? 'info' : (planType === 'LAUNCH_OFFER' ? 'success' : 'neutral')}
                                    size="md"
                                    className="font-semibold"
                                >
                                    {planType === 'PRO' && <Crown className="w-4 h-4 mr-1" />}
                                    {planType === 'LAUNCH_OFFER' && <Sparkles className="w-4 h-4 mr-1" />}
                                    {displayPlanName}
                                </Badge>
                                {planType === 'PRO' && isSubscriptionActive && (
                                    <Badge variant="success" size="sm">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        {t('admin.subscription.active', 'Actif')}
                                    </Badge>
                                )}
                                {planType === 'LAUNCH_OFFER' && (
                                    <Badge variant="warning" size="sm">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {t('admin.subscription.limitedOffer', 'Offre limitée')}
                                    </Badge>
                                )}
                                {isCustomPlan && (
                                    <Badge variant="warning" size="sm">
                                        <Sparkles className="w-3 h-3 mr-1" />
                                        {t('admin.subscription.customPlan', 'Plan Personnalisé')}
                                    </Badge>
                                )}
                            </div>

                            {/* Prix */}
                            <div className="mb-4">
                                <span className="text-3xl font-bold text-foreground">
                                    {displayPrice}€
                                </span>
                                <span className="text-foreground-secondary">/{t('common.month', 'mois')}</span>
                                {isCustomPlan && displayPrice === 0 && (
                                    <span className="ml-2 text-sm text-success">({t('common.free', 'Gratuit')})</span>
                                )}
                            </div>

                            {/* Commission actuelle */}
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <TrendingUp className="w-4 h-4 text-foreground-secondary" />
                                <span className="text-sm text-foreground-secondary">
                                    {t('admin.subscription.currentCommission', 'Commission CashLive actuelle :')}
                                </span>
                                <span className="font-semibold text-foreground">
                                    {configuredCommissionRate}%
                                </span>
                                {/* Badge taux négocié */}
                                {isCustomPlan && (
                                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                                        {t('admin.subscription.negotiatedRate', 'taux négocié')}
                                    </span>
                                )}
                            </div>

                            {/* Expiration de l'offre custom active */}
                            {isCustomPlan && subscriptionConfig?.customPlanExpiresAt && (
                                <div className="mt-4 flex items-center gap-2 p-3 bg-warning/5 rounded-lg border border-warning/20 max-w-2xl">
                                    <Clock className="w-4 h-4 text-warning flex-shrink-0" />
                                    <p className="text-xs text-foreground-secondary">
                                        <span className="font-bold text-warning uppercase">Offre limitée :</span>{' '}
                                        {t('admin.subscription.activeOfferExpiresInfo', 'Cette offre personnalisée expire le {{date}}. À cette date, vous repasserez automatiquement au plan Freemium.', {
                                            date: new Date(subscriptionConfig.customPlanExpiresAt).toLocaleDateString()
                                        })}
                                    </p>
                                </div>
                            )}
                            {/* Information Offre de Lancement */}
                            {planType === 'LAUNCH_OFFER' && (
                                <div className="mt-4 flex items-center gap-2 p-3 bg-success/5 rounded-lg border border-success/20 max-w-2xl">
                                    <Gift className="w-4 h-4 text-success flex-shrink-0" />
                                    <p className="text-xs text-foreground-secondary" dangerouslySetInnerHTML={{ __html: t('admin.subscription.launchOfferInfo', 'Offre de lancement exclusive<br />Votre compte a été créé pendant la période promotionnelle (30 janvier – 30 mai 2026).<br />Vous bénéficiez de <span class="font-bold text-success">0% de commission jusqu\'au 30 mai 2026</span>, puis d\'un taux préférentiel de <span class="font-bold text-success">2,5%</span>.') }} />
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        {planType === 'PRO' && displayPrice > 0 && (
                            <div className="flex flex-col gap-3 lg:items-end">
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={handleManageSubscription}
                                    className="w-full lg:w-auto"
                                >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    {t('admin.subscription.manageSubscription', 'Gérer mon abonnement')}
                                </Button>
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>

            {/* Offre personnalisée en attente */}
            {hasPendingOffer && pendingOffer && (
                <Card className="border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background to-purple-500/10 relative overflow-hidden mb-8 shadow-xl shadow-amber-500/10 group">

                    {/* Effet de brillance animé en haut */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-50" />

                    {/* Décoration de fond */}
                    <div className="absolute -top-10 -right-10 p-4 opacity-10 pointer-events-none rotate-12 transition-transform duration-700 group-hover:rotate-6 group-hover:scale-110">
                        <Gift className="w-48 h-48 text-amber-500" />
                    </div>

                    <CardBody className="p-8 relative z-10">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="flex-1 space-y-4">
                                <div>
                                    <Badge variant="warning" className="mb-3 font-bold px-3 py-1 text-xs uppercase tracking-wider bg-amber-500/20 text-amber-600 border-amber-500/30">
                                        {t('admin.subscription.limitedOffer', 'Offre Exclusive')}
                                    </Badge>
                                    <h3 className="text-2xl font-bold text-foreground flex items-center gap-3">
                                        {t('admin.subscription.specialOfferTitle', 'Une offre spéciale vous attend !')}
                                        <Sparkles className="w-6 h-6 text-amber-500 animate-pulse" />
                                    </h3>
                                    <p className="text-lg text-foreground-secondary mt-2 max-w-xl">
                                        {t('admin.subscription.specialOfferDesc', 'CashLive vous propose de passer au plan')} <span className="font-bold text-foreground bg-gradient-to-r from-amber-600 to-purple-600 bg-clip-text text-transparent px-1">{pendingOffer.customPlanName}</span>.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 bg-card/50 p-4 rounded-xl border border-border/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                                            <CheckCircle className="w-5 h-5 text-success" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-foreground-secondary uppercase tracking-wide font-semibold">{t('admin.subscription.price', 'Prix')}</p>
                                            <p className="font-bold text-foreground text-lg">{pendingOffer.customPrice}€<span className="text-sm font-normal text-foreground-secondary">/{t('common.month', 'mois')}</span></p>
                                        </div>
                                    </div>

                                    {pendingOffer.expiresAt && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                                                <div className="w-2 h-2 rounded-full bg-error animate-ping" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-error uppercase tracking-wide font-black">{t('admin.subscription.expiresAtShort', 'Expire le')}</p>
                                                <p className="font-bold text-error text-lg">{new Date(pendingOffer.expiresAt).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                                            <CheckCircle className="w-5 h-5 text-success" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-foreground-secondary uppercase tracking-wide font-semibold">{t('admin.subscription.commission', 'Commission')}</p>
                                            <p className="font-bold text-foreground text-lg">{pendingOffer.customCommissionRate}%</p>
                                        </div>
                                    </div>

                                    {/* Fonctionnalités supplémentaires */}
                                    {pendingOffer.customFeatures.iaAssistant && (
                                        <div className="flex items-center gap-2 sm:col-span-2 text-sm text-foreground-secondary pt-2 border-t border-border/50 mt-1">
                                            <Brain className="w-4 h-4 text-purple-500" />
                                            <span>{t('admin.subscription.iaIncluded', 'Assistant IA Illimité inclus')}</span>
                                        </div>
                                    )}
                                    {pendingOffer.customFeatures.savChat && (
                                        <div className="flex items-center gap-2 sm:col-span-2 text-sm text-foreground-secondary">
                                            <MessageSquare className="w-4 h-4 text-blue-500" />
                                            <span>{t('admin.subscription.savChatIncluded', 'Module Chat SAV Client inclus')}</span>
                                        </div>
                                    )}
                                    {pendingOffer.customFeatures.savHistory && (
                                        <div className="flex items-center gap-2 sm:col-span-2 text-sm text-foreground-secondary">
                                            <Infinity className="w-4 h-4 text-indigo-500" />
                                            <span>{t('admin.subscription.savHistoryIncluded', 'Historique des conversations illimité')}</span>
                                        </div>
                                    )}

                                    {/* Info Fallback Freemium */}
                                    <div className="sm:col-span-2 mt-4 p-3 bg-foreground/5 rounded-lg border border-border/50">
                                        <p className="text-xs text-foreground-secondary leading-relaxed italic">
                                            {t('admin.subscription.fallbackFreemiumInfo', 'Note : À l\'issue de cette offre personnalisée, votre organisation repassera automatiquement au plan Freemium standard si aucune autre offre n\'est configurée.')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-shrink-0 w-full md:w-auto flex flex-col items-stretch gap-3">
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={handleSubscribeToCustomPlan}
                                    isLoading={isUpgrading}
                                    className="w-full md:min-w-[240px] shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-105 transition-all duration-300 text-base py-6"
                                >
                                    {t('admin.subscription.acceptAndSubscribe', 'Accepter et souscrire')}
                                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <p className="text-xs text-center text-foreground-secondary opacity-70">
                                    {t('admin.subscription.securePayment', 'Paiement sécurisé via Stripe')}
                                </p>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}

            {/* Comparaison des plans - Visible si FREEMIUM pour pousser au PRO */}
            {
                planType === 'FREEMIUM' && (
                    <Card variant="default" className="border-primary/20 bg-primary/5">
                        <CardBody className="p-4">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                {/* Titre + Features */}
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-primary" />
                                        {t('admin.subscription.whyPro', 'Pourquoi passer au PRO ?')}
                                    </h3>

                                    <div className="flex flex-wrap gap-3">
                                        {/* Commission réduite */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <TrendingUp className="w-4 h-4 text-primary" />
                                            <span className="text-foreground-secondary">{t('admin.subscription.proFeatures.commission', '2% commission')}</span>
                                        </div>

                                        {/* IA illimitée */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <Zap className="w-4 h-4 text-primary" />
                                            <span className="text-foreground-secondary">{t('admin.subscription.proFeatures.unlimitedAI', 'IA illimitée')}</span>
                                        </div>

                                        {/* SAV complet */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <MessageSquare className="w-4 h-4 text-primary" />
                                            <span className="text-foreground-secondary">{t('admin.subscription.proFeatures.fullSAV', 'SAV complet')}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* CTA */}
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleUpgradeToPro}
                                    disabled={isUpgrading}
                                    className="whitespace-nowrap"
                                >
                                    {isUpgrading ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Crown className="w-4 h-4 mr-2" />
                                    )}
                                    {t('admin.subscription.startProNow', 'PRO - 59€/mois')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                )
            }

            {/* Historique des factures — regroupées par mois */}
            {(() => {
                // Fusion : allInvoices (Firestore) + billingStatements legacy (HTTP, sans invoice interne)
                const commissionPeriods = new Set(
                    allInvoices
                        .filter(inv => inv.type === 'commissions_monthly' && inv.periodLabel)
                        .map(inv => inv.periodLabel!)
                );
                const legacyItems: InternalInvoice[] = billingStatements
                    .filter(s => (s.total ?? 0) > 0 && !commissionPeriods.has(s.periodMonth))
                    .map(s => ({
                        id: `stmt-${s.id}`,
                        invoiceNumber: s.invoice?.invoiceNumber ?? '',
                        type: 'commissions_monthly' as const,
                        status: s.status === 'PAID' ? 'paid' : s.status === 'INVOICED' ? 'issued' : 'issued',
                        issueDate: s.createdAt ? new Date(s.createdAt as string) : new Date(`${s.periodMonth}-01`),
                        periodLabel: s.periodMonth,
                        totalCents: s.total ?? 0,
                        totalFormatted: s.totalFormatted,
                        subtotalCents: s.subtotal ?? 0,
                        vatAmountCents: s.vatAmount ?? 0,
                        stripeHostedInvoiceUrl: s.invoice?.stripeHostedInvoiceUrl ?? null,
                        stripePdfUrl: s.invoice?.stripePdfUrl ?? null,
                        stripeInvoiceId: s.invoice?.stripeInvoiceId ?? null,
                        stripeInvoiceStatus: s.invoice?.stripeInvoiceStatus ?? null,
                        paidAt: s.paidAt ? new Date(s.paidAt as string) : null,
                        paymentIntentId: null,
                    }));

                const merged = [...allInvoices, ...legacyItems].sort((a, b) => {
                    const da = a.issueDate ? new Date(a.issueDate).getTime() : 0;
                    const db2 = b.issueDate ? new Date(b.issueDate).getTime() : 0;
                    return db2 - da;
                });

                // Regroupement par YYYY-MM
                const grouped: Record<string, InternalInvoice[]> = {};
                for (const inv of merged) {
                    const d = inv.issueDate ? new Date(inv.issueDate) : new Date();
                    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(inv);
                }
                const sortedMonths = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

                const typeLabelMap: Record<string, string> = {
                    credits_purchase: t('invoice.type.credits', 'Achat de crédits'),
                    shipping_payg:    t('invoice.type.shipping', 'Génération de colis'),
                    commissions_monthly: t('invoice.type.commissions', 'Commissions mensuelles'),
                };
                const statusVariantMap: Record<string, 'success' | 'info' | 'error' | 'neutral'> = {
                    paid: 'success', issued: 'info', cancelled: 'error',
                };

                return (
                    <Card variant="default">
                        <CardBody className="p-5 space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-primary" />
                                        {t('admin.subscription.billingHistory.title', 'Historique des factures')}
                                    </h3>
                                    <p className="text-xs text-foreground-secondary mt-1">
                                        {t('admin.subscription.billingHistory.subtitle', 'Toutes vos factures, regroupées par mois.')}
                                    </p>
                                </div>
                                {billingLoading && <Loader2 className="w-4 h-4 animate-spin text-foreground-secondary" />}
                            </div>

                            {!billingLoading && merged.length === 0 && (
                                <p className="text-xs text-foreground-secondary italic">
                                    {t('admin.subscription.billingHistory.noInvoices', 'Aucune facture pour le moment.')}
                                </p>
                            )}

                            {billingLoading && merged.length === 0 && (
                                <div className="flex items-center gap-2 py-2">
                                    <Loader2 className="w-3 h-3 animate-spin text-foreground-secondary" />
                                    <span className="text-xs text-foreground-secondary">{t('common.loading', 'Chargement…')}</span>
                                </div>
                            )}

                            <div className="space-y-3">
                                {sortedMonths.map(monthKey => {
                                    const [year, month] = monthKey.split('-').map(Number);
                                    const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(i18n.language, {
                                        year: 'numeric', month: 'long',
                                    });
                                    const isMonthExpanded = expandedMonths.has(monthKey);
                                    const monthTotal = grouped[monthKey].reduce((sum, inv) => sum + inv.totalCents, 0);
                                    const count = grouped[monthKey].length;

                                    return (
                                        <div key={monthKey} className="border border-border rounded-xl overflow-hidden">
                                            {/* Header du mois — cliquable */}
                                            <button
                                                type="button"
                                                onClick={() => setExpandedMonths(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(monthKey)) next.delete(monthKey);
                                                    else next.add(monthKey);
                                                    return next;
                                                })}
                                                className="w-full flex items-center justify-between px-4 py-3 bg-background-secondary/60 hover:bg-background-secondary transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {isMonthExpanded
                                                        ? <ChevronDown className="w-4 h-4 text-foreground-secondary shrink-0" />
                                                        : <ChevronRight className="w-4 h-4 text-foreground-secondary shrink-0" />
                                                    }
                                                    <span className="text-sm font-semibold text-foreground capitalize">
                                                        {monthLabel}
                                                    </span>
                                                    <span className="text-xs text-foreground-secondary">
                                                        {t('admin.subscription.billingHistory.invoiceCount', '{{count}} facture', { count })} · {(monthTotal / 100).toFixed(2)} €
                                                    </span>
                                                </div>
                                            </button>

                                            {/* Liste des factures du mois */}
                                            {isMonthExpanded && (
                                            <div className="divide-y divide-border">
                                            {grouped[monthKey].map(inv => {
                                                const isExpanded = expandedStatementId === inv.id;
                                                const hostedUrl = inv.stripeHostedInvoiceUrl || null;
                                                const pdfUrl = inv.stripePdfUrl || null;
                                                const invoiceUrl = hostedUrl || pdfUrl;
                                                const hasInvoice = !!invoiceUrl;
                                                const typeLabel = typeLabelMap[inv.type] ?? inv.type;
                                                const statusVariant = statusVariantMap[inv.status] ?? 'neutral';

                                                return (
                                                    <div
                                                        key={inv.id}
                                                        className="bg-background"
                                                    >
                                                        <div className="flex items-center">
                                                            {/* Zone cliquable principale — expand/collapse */}
                                                            <button
                                                                type="button"
                                                                onClick={() => setExpandedStatementId(isExpanded ? null : inv.id)}
                                                                className="flex-1 flex items-center justify-between px-4 py-3 text-left hover:bg-background-secondary/60 transition-colors min-w-0"
                                                            >
                                                                <div className="flex items-center gap-3 flex-wrap min-w-0">
                                                                    <FileText className="w-4 h-4 text-foreground-secondary shrink-0" />
                                                                    <span className="text-sm font-medium text-foreground">{typeLabel}</span>
                                                                    {inv.invoiceNumber && (
                                                                        <span className="font-mono text-xs text-foreground-secondary">{inv.invoiceNumber}</span>
                                                                    )}
                                                                    <span className="text-xs text-foreground-secondary">{inv.totalFormatted}</span>
                                                                    <Badge size="sm" variant={statusVariant}>{inv.status}</Badge>
                                                                </div>
                                                                <span className="text-xs text-foreground-secondary shrink-0 ml-3">
                                                                    {isExpanded ? t('common.collapse', 'Réduire') : t('common.expand', 'Détail')}
                                                                </span>
                                                            </button>

                                                            {/* Accès rapide PDF depuis la ligne */}
                                                            {(pdfUrl || hostedUrl) && (
                                                                <a
                                                                    href={pdfUrl ?? hostedUrl ?? '#'}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={pdfUrl ? t('admin.subscription.billingHistory.downloadPdf', 'Télécharger le PDF') : t('admin.subscription.billingHistory.openInvoice', 'Voir la facture')}
                                                                    onClick={e => e.stopPropagation()}
                                                                    className="shrink-0 self-stretch flex items-center justify-center px-3 border-l border-border text-foreground-secondary hover:text-primary hover:bg-primary/5 transition-colors"
                                                                >
                                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                                </a>
                                                            )}
                                                        </div>

                                                        {isExpanded && (
                                                            <div className="px-4 pb-4 pt-2 border-t border-border bg-background">
                                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                                    <div className="space-y-1 text-xs">
                                                                        {inv.invoiceNumber && (
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.invoiceNumber', 'N° facture :')}</span>
                                                                                <span className="font-mono font-medium text-foreground">{inv.invoiceNumber}</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.date', 'Date :')}</span>
                                                                            <span className="font-medium text-foreground">
                                                                                {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString(i18n.language) : '—'}
                                                                            </span>
                                                                        </div>
                                                                        {inv.periodLabel && (
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.period', 'Période :')}</span>
                                                                                <span className="font-medium text-foreground">{inv.periodLabel}</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.subtotal', 'Sous-total HT :')}</span>
                                                                            <span className="font-medium text-foreground">{(inv.subtotalCents / 100).toFixed(2)} €</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.vat', 'TVA :')}</span>
                                                                            <span className="font-medium text-foreground">{(inv.vatAmountCents / 100).toFixed(2)} €</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.total', 'Total TTC :')}</span>
                                                                            <span className="font-semibold text-foreground">{inv.totalFormatted}</span>
                                                                        </div>
                                                                        {inv.paidAt && (
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-foreground-secondary">{t('admin.subscription.billingHistory.paidAt', 'Payée le :')}</span>
                                                                                <span className="font-medium text-success">{new Date(inv.paidAt).toLocaleDateString(i18n.language)}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-col gap-2">
                                                                        {hostedUrl && (
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() => window.open(hostedUrl, '_blank', 'noopener,noreferrer')}
                                                                            >
                                                                                <ExternalLink className="w-3 h-3 mr-1.5" />
                                                                                {t('admin.subscription.billingHistory.openInvoiceOnline', 'Facture en ligne')}
                                                                            </Button>
                                                                        )}
                                                                        {pdfUrl && (
                                                                            <Button
                                                                                variant="primary"
                                                                                size="sm"
                                                                                onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                                                                            >
                                                                                <ExternalLink className="w-3 h-3 mr-1.5" />
                                                                                {t('admin.subscription.billingHistory.downloadPdf', 'Télécharger PDF')}
                                                                            </Button>
                                                                        )}
                                                                        {!hasInvoice && inv.status !== 'paid' && (
                                                                            <span className="text-xs text-foreground-secondary italic py-1">
                                                                                {t('admin.subscription.billingHistory.invoicePending', 'Facture en cours de génération')}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </CardBody>
                    </Card>
                );
            })()}

            {/* IA Assistant Status - Now showing unified credits */}
            {isLiveAssistantEnabled(idorga, configuration) && (
                <Card variant="outlined" className="border-border bg-surface transition-all hover:border-primary/20">
                    <CardBody className="p-5">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                            <div className="flex items-center gap-5">
                                <div className="p-3 rounded-2xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                                    <Brain className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-foreground">
                                        {t('admin.ia.inAppCredits', 'Crédits In-App')}
                                    </h3>
                                    <div className="flex items-center gap-6 mt-1.5">
                                        {isIAUnlimited ? (
                                            <div className="flex items-center gap-2">
                                                <Infinity className="w-5 h-5 text-primary" />
                                                <span className="text-sm font-bold text-primary italic">
                                                    {t('admin.ia.unlimited', 'Accès Illimité (Inclus PRO)')}
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] text-foreground-secondary uppercase font-bold tracking-wider leading-none mb-1">{t('admin.ia.inAppBalance', 'Solde crédits in-app')}</span>
                                                    <div className="flex items-baseline gap-1">
                                                        <span className={`text-lg font-black ${creditsBalanceCents < 500 ? 'text-error' : 'text-foreground'}`}>
                                                            {(creditsBalanceCents / 100).toFixed(2)}€
                                                        </span>
                                                        {creditsLoading && <Loader2 className="w-3 h-3 animate-spin text-foreground-secondary" />}
                                                    </div>
                                                </div>
                                                <div className="h-8 w-px bg-border/50 hidden sm:block" />
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] text-foreground-secondary uppercase font-bold tracking-wider leading-none mb-1">{t('admin.ia.usedAI', 'Utilisé (IA)')}</span>
                                                    <div className="flex items-baseline gap-1">
                                                        <span className="text-lg font-black text-secondary">
                                                            {(creditsUsedForAICents / 100).toFixed(2)}€
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="h-8 w-px bg-border/50 hidden sm:block" />
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] text-foreground-secondary uppercase font-bold tracking-wider leading-none mb-1">{t('admin.ia.usedShipping', 'Utilisé (Expédition)')}</span>
                                                    <div className="flex items-baseline gap-1">
                                                        <span className="text-lg font-black text-primary">
                                                            {(creditsUsedForShippingCents / 100).toFixed(2)}€
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                {!isIAUnlimited && (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={handleBuyCredits}
                                        leftIcon={<ShoppingCart className="w-4 h-4" />}
                                        className="w-full lg:w-auto px-6 font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
                                    >
                                        {t('admin.ia.buyCredits', 'Recharger des crédits')}
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={() => navigate(`/orga/${idorga}/admin/shipping-credits`)}
                                    leftIcon={<ExternalLink className="w-4 h-4" />}
                                    className="w-full lg:w-auto px-6 font-bold"
                                >
                                    {t('admin.ia.viewHistory', "Voir l'historique")}
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}



            {/* ========== SECTION SUPER ADMIN ========== */}
            {
                isSuperAdmin && (
                    <Card variant="default" className="border-2 border-dashed border-warning/50 bg-warning/5">
                        <CardBody className="p-4">
                            <div
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => setShowSuperAdminPanel(!showSuperAdminPanel)}
                            >
                                <div className="flex items-center gap-2">
                                    <Crown className="w-5 h-5 text-warning" />
                                    <span className="font-semibold text-warning">{t('admin.subscription.superAdmin', 'Super Admin')}</span>
                                    {subscriptionConfig?.isCustomPlan && (
                                        <Badge variant="warning" size="sm">{t('admin.subscription.customPlanActive', 'Plan Personnalisé Actif')}</Badge>
                                    )}
                                </div>
                                <span className="text-sm text-foreground-secondary">
                                    {showSuperAdminPanel ? t('admin.subscription.collapse', '▲ Réduire') : t('admin.subscription.defineCustomPlan', '▼ Définir un abonnement personnalisé')}
                                </span>
                            </div>

                            {showSuperAdminPanel && (
                                <div className="mt-4 space-y-4">
                                    {/* Afficher le plan custom actuel s'il existe */}
                                    {subscriptionConfig?.isCustomPlan && (
                                        <div className="p-3 bg-warning/10 rounded-lg border border-warning/30">
                                            <p className="text-sm font-medium text-warning mb-2">{t('admin.subscription.currentPlan', 'Plan actuel:')}</p>
                                            <ul className="text-xs text-foreground-secondary space-y-1">
                                                <li>• {t('admin.subscription.customPlanDetails.name', 'Nom')}: {subscriptionConfig.customPlanName || t('admin.subscription.customPlan', 'Plan Personnalisé')}</li>
                                                <li>• {t('admin.subscription.customPlanDetails.price', 'Prix')}: {subscriptionConfig.customPrice ?? 0}€/{t('common.month', 'mois')}</li>
                                                <li>• {t('admin.subscription.customPlanDetails.commission', 'Commission')}: {subscriptionConfig.customCommissionRate ?? 3.0}%</li>
                                                <li>• {t('admin.subscription.customPlanDetails.definedBy', 'Défini par')}: {subscriptionConfig.customPlanSetBy}</li>
                                                {subscriptionConfig.customPlanExpiresAt && (
                                                    <li className="text-error font-bold">• Expire le: {new Date(subscriptionConfig.customPlanExpiresAt).toLocaleDateString()}</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Formulaire */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-foreground-secondary mb-1">
                                                Nom du plan
                                            </label>
                                            <input
                                                type="text"
                                                value={inputCustomPlanName}
                                                onChange={(e) => setInputCustomPlanName(e.target.value)}
                                                placeholder="Ex: Partenaire VIP"
                                                className="w-full px-3 py-2 text-sm bg-background-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-warning"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-foreground-secondary mb-1">
                                                Prix mensuel (€)
                                            </label>
                                            <input
                                                type="number"
                                                value={inputCustomPrice}
                                                onChange={(e) => setInputCustomPrice(Number(e.target.value))}
                                                min={0}
                                                className="w-full px-3 py-2 text-sm bg-background-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-warning"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-foreground-secondary mb-1">
                                                Taux de commission (%)
                                            </label>
                                            <input
                                                type="number"
                                                value={inputCustomCommissionRate}
                                                onChange={(e) => setInputCustomCommissionRate(Number(e.target.value))}
                                                min={0}
                                                max={10}
                                                step={0.1}
                                                className="w-full px-3 py-2 text-sm bg-background-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-warning"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-foreground-secondary mb-1">
                                                Note interne
                                            </label>
                                            <input
                                                type="text"
                                                value={inputCustomPlanNote}
                                                onChange={(e) => setInputCustomPlanNote(e.target.value)}
                                                placeholder="Raison du plan spécial..."
                                                className="w-full px-3 py-2 text-sm bg-background-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-warning"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-foreground-secondary mb-1">
                                                Date d'expiration (Optionnel)
                                            </label>
                                            <input
                                                type="date"
                                                value={inputCustomPlanExpiresAt}
                                                onChange={(e) => setInputCustomPlanExpiresAt(e.target.value)}
                                                className="w-full px-3 py-2 text-sm bg-background-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-warning"
                                            />
                                            <p className="text-[10px] text-warning mt-1 italic">
                                                {t('admin.subscription.willReturnToFreemium', '* L\'organisation repassera en Freemium après cette date.')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div>
                                        <label className="block text-xs font-medium text-foreground-secondary mb-2">
                                            Fonctionnalités incluses
                                        </label>
                                        <div className="flex flex-wrap gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={inputCustomFeatures.iaAssistant}
                                                    onChange={(e) => setInputCustomFeatures({ ...inputCustomFeatures, iaAssistant: e.target.checked })}
                                                    className="w-4 h-4 rounded border-border text-warning focus:ring-warning"
                                                />
                                                <span className="text-sm">IA Assistant</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={inputCustomFeatures.savChat}
                                                    onChange={(e) => setInputCustomFeatures({ ...inputCustomFeatures, savChat: e.target.checked })}
                                                    className="w-4 h-4 rounded border-border text-warning focus:ring-warning"
                                                />
                                                <span className="text-sm">Chat SAV</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={inputCustomFeatures.savHistory}
                                                    onChange={(e) => setInputCustomFeatures({ ...inputCustomFeatures, savHistory: e.target.checked })}
                                                    className="w-4 h-4 rounded border-border text-warning focus:ring-warning"
                                                />
                                                <span className="text-sm">Historique SAV</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Boutons d'action */}
                                    <div className="flex gap-3 pt-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            isLoading={isSavingCustomPlan}
                                            onClick={async () => {
                                                try {
                                                    setIsSavingCustomPlan(true);
                                                    const response = await fetch(`${FUNCTIONS_BASE_URL}/setCustomSubscription`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({
                                                            organizationId: configuration?.organizationId || idorga,
                                                            superAdminId: currentUser?.uid,
                                                            customPlanName: inputCustomPlanName || 'Plan Personnalisé',
                                                            customPrice: inputCustomPrice,
                                                            customCommissionRate: inputCustomCommissionRate,
                                                            customFeatures: inputCustomFeatures,
                                                            customPlanNote: inputCustomPlanNote,
                                                            expiresAt: inputCustomPlanExpiresAt ? new Date(inputCustomPlanExpiresAt).toISOString() : undefined,
                                                        }),
                                                    });
                                                    const data = await response.json();
                                                    if (!response.ok) {
                                                        throw new Error(data.error || data.hint || `HTTP ${response.status}`);
                                                    }
                                                    if (data.success) {
                                                        toast.success(data.message || 'Abonnement personnalisé activé !');
                                                        refreshConfiguration();
                                                        setShowSuperAdminPanel(false);
                                                    } else {
                                                        throw new Error(data.error || 'Échec de la configuration');
                                                    }
                                                } catch (error) {
                                                    const message = error instanceof Error ? error.message : 'Erreur lors de la configuration';
                                                    toast.error(message);
                                                    console.error(error);
                                                } finally {
                                                    setIsSavingCustomPlan(false);
                                                }
                                            }}
                                        >
                                            <Crown className="w-4 h-4 mr-1" />
                                            Appliquer le plan personnalisé
                                        </Button>

                                        {subscriptionConfig?.isCustomPlan && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={async () => {
                                                    if (!confirm('Supprimer le plan personnalisé et repasser en FREEMIUM ?')) return;
                                                    try {
                                                        setIsSavingCustomPlan(true);
                                                        const response = await fetch(`${FUNCTIONS_BASE_URL}/removeCustomSubscription`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({
                                                                organizationId: configuration?.organizationId || idorga,
                                                                superAdminId: currentUser?.uid,
                                                            }),
                                                        });
                                                        const data = await response.json();
                                                        if (data.success) {
                                                            toast.success('Plan personnalisé supprimé, retour en FREEMIUM');
                                                            refreshConfiguration();
                                                        } else {
                                                            throw new Error(data.error);
                                                        }
                                                    } catch (error) {
                                                        toast.error('Erreur lors de la suppression');
                                                        console.error(error);
                                                    } finally {
                                                        setIsSavingCustomPlan(false);
                                                    }
                                                }}
                                            >
                                                Supprimer le plan custom
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )
            }

            {/* Buy Credits Modal */}
            {idorga && (
                <BuyCreditsModal
                    isOpen={showBuyCreditsModal}
                    onClose={() => setShowBuyCreditsModal(false)}
                    organizationId={idorga}
                    currentBalance={creditsBalanceCents}
                    onSuccess={handleBuyCreditsSuccess}
                />
            )}
        </div >
    );
};

export default SubscriptionPlanSection;
